import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoryService, Category } from '../../services/categoryService';
import { GUEST_BLOG_SITE_CATEGORIES } from '../../types/guestBlogSite';
import { apiService } from '../../services/api';

const CategorySettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  // Format date to readable format: "20 March 2026, 10:30 AM"
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const year = date.getFullYear();
      const time = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      return `${day} ${month} ${year}, ${time}`;
    } catch (error) {
      return dateString;
    }
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [newValueInput, setNewValueInput] = useState('');
  const [sitesUsingCategory, setSitesUsingCategory] = useState<number>(0);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    description: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Convert existing hardcoded categories to display format
  const existingCategories: Category[] = GUEST_BLOG_SITE_CATEGORIES.map((cat, index) => ({
    id: `cat-${index + 1}`,
    value: cat.value,
    label: cat.label,
    description: '',
    createdAt: new Date().toISOString().split('T')[0],
    updatedAt: new Date().toISOString().split('T')[0],
  }));

  // Fetch categories from API (will use existing categories as fallback)
  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['categories', page, rowsPerPage, search],
    queryFn: () => categoryService.getCategories({ page: page + 1, limit: rowsPerPage, search }),
  });

  // Use API data if available, otherwise filter existing hardcoded categories
  const filteredExisting = search
    ? existingCategories.filter(cat =>
        cat.value.toLowerCase().includes(search.toLowerCase()) ||
        cat.label.toLowerCase().includes(search.toLowerCase())
      )
    : existingCategories;
  const categories = categoriesData?.data || filteredExisting.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalCount = categoriesData?.pagination?.total || filteredExisting.length;

  // Create category mutation
  const createMutation = useMutation({
    mutationFn: categoryService.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Category added successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to add category',
        severity: 'error',
      });
    },
  });

  // Update category mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => categoryService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Category updated successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update category',
        severity: 'error',
      });
    },
  });

  // Delete category mutation
  const deleteMutation = useMutation({
    mutationFn: categoryService.deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setSnackbar({
        open: true,
        message: 'Category deleted successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete category',
        severity: 'error',
      });
    },
  });

  // Rename category mutation
  const renameMutation = useMutation({
    mutationFn: ({ id, newValue }: { id: string; newValue: string }) =>
      categoryService.renameCategory(id, newValue),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
      setDialogOpen(false);
      setNewValueInput('');

      const sitesCount = data.sitesUpdated || 0;
      const oldValue = data.oldValue || '';
      const newValue = data.newValue || '';

      setSnackbar({
        open: true,
        message: `Category renamed from "${oldValue}" to "${newValue}". ${sitesCount} guest blog site${sitesCount !== 1 ? 's' : ''} ${sitesCount !== 1 ? 'have' : 'has'} been updated.`,
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to rename category',
        severity: 'error',
      });
    },
  });

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({ value: '', label: '', description: '' });
    setDialogOpen(true);
  };

  const handleEdit = async (category: Category) => {
    setEditingCategory(category);
    setFormData({
      value: category.value,
      label: category.label,
      description: category.description || '',
    });
    setNewValueInput(category.value);
    setDialogOpen(true);

    // Fetch count of sites using this category
    try {
      const response = await apiService.get<{ pagination?: { total: number } }>(`/guest-sites?category=${category.value}&limit=1`);
      setSitesUsingCategory(response.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching sites count:', error);
      setSitesUsingCategory(0);
    }
  };

  const handleDelete = (category: Category) => {
    if (window.confirm(`Are you sure you want to delete the category "${category.label}"?`)) {
      deleteMutation.mutate(category.id);
    }
  };

  const handleSubmit = () => {
    if (!formData.value.trim() || !formData.label.trim()) {
      setSnackbar({
        open: true,
        message: 'Value and Label are required',
        severity: 'error',
      });
      return;
    }

    if (editingCategory) {
      // Check if user wants to rename the value
      const valueChanged = newValueInput.trim() && newValueInput !== formData.value;

      if (valueChanged) {
        // Confirm rename operation
        const formattedNewValue = newValueInput.toUpperCase().replace(/[^A-Z0-9]/g, '_');
        const sitesMessage = sitesUsingCategory > 0
          ? `\n\nThis will update ${sitesUsingCategory} guest blog site${sitesUsingCategory !== 1 ? 's' : ''}.`
          : '\n\nNo guest blog sites are currently using this category.';

        if (window.confirm(
          `Are you sure you want to rename "${formData.value}" to "${formattedNewValue}"?${sitesMessage}`
        )) {
          // Perform rename first, then update label/description
          renameMutation.mutate({
            id: editingCategory.id,
            newValue: newValueInput
          });

          // Update label/description after rename
          setTimeout(() => {
            updateMutation.mutate({
              id: editingCategory.id,
              data: {
                label: formData.label,
                description: formData.description,
              },
            });
          }, 500);
        }
      } else {
        // Just update label and description
        updateMutation.mutate({
          id: editingCategory.id,
          data: {
            label: formData.label,
            description: formData.description,
          },
        });
      }
    } else {
      createMutation.mutate({
        value: formData.value,
        label: formData.label,
        description: formData.description,
      });
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Box sx={{ width: '100%', p: 3 }}>
        {/* Back link */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/settings')}
          sx={{ textTransform: 'none', color: '#64748b', mb: 2, '&:hover': { color: '#1e293b' } }}
        >
          Back to Settings
        </Button>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
              Blog Site Categories
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Manage categories used in guest blog sites
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              background: '#3b82f6',
              '&:hover': { background: '#2563eb' },
              textTransform: 'none',
              px: 3,
            }}
          >
            Add Category
          </Button>
        </Box>

        {/* Categories Section */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Box sx={{ p: 2, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Categories List
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  {totalCount} {totalCount === 1 ? 'category' : 'categories'} total
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                background: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                padding: '6px 10px',
                minWidth: '280px',
                transition: 'all 0.15s',
                '&:focus-within': {
                  background: '#FFFFFF',
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59,130,246,0.08)'
                }
              }}>
                <SearchIcon sx={{ width: '16px', height: '16px', color: '#9CA3AF', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search by value or label..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  style={{
                    border: 'none',
                    background: 'none',
                    outline: 'none',
                    fontFamily: 'inherit',
                    fontSize: '13px',
                    color: '#0F172A',
                    width: '100%',
                  }}
                />
                {search && (
                  <CloseIcon
                    onClick={() => {
                      setSearch('');
                      setPage(0);
                    }}
                    sx={{
                      width: '16px',
                      height: '16px',
                      color: '#9CA3AF',
                      cursor: 'pointer',
                      flexShrink: 0,
                      '&:hover': { color: '#6B7280' }
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Label</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Updated</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: '#1e293b' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No categories found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell sx={{ color: '#475569' }}>{category.value}</TableCell>
                      <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{category.label}</TableCell>
                      <TableCell sx={{ color: '#64748b' }}>
                        {category.description || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#64748b', fontSize: '13px' }}>
                        {formatDateTime(category.createdAt)}
                      </TableCell>
                      <TableCell sx={{ color: '#64748b', fontSize: '13px' }}>
                        {formatDateTime(category.updatedAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(category)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(category)}
                            color="error"
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Paper>
      </Box>

      {/* Add/Edit Category Dialog */}
      {dialogOpen && (
        <div className="category-modal-overlay" onClick={(e) => e.target === e.currentTarget && setDialogOpen(false)}>
          <div className="category-modal" onClick={(e) => e.stopPropagation()}>
            <div className="category-modal-header">
              <h2 className="category-modal-title">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
              <button className="category-modal-close-btn" onClick={() => setDialogOpen(false)}>
                <CloseIcon />
              </button>
            </div>

            <div className="category-modal-content">
              {editingCategory && sitesUsingCategory > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{sitesUsingCategory} guest blog site{sitesUsingCategory !== 1 ? 's are' : ' is'}</strong> currently using this category.
                </Alert>
              )}

              <TextField
                fullWidth
                label="Value *"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="e.g., web3, crypto, ai_tools"
                margin="normal"
                helperText={editingCategory ? "Unique identifier (will be auto-formatted to UPPERCASE_WITH_UNDERSCORES)" : "Unique identifier (will be auto-formatted to UPPERCASE_WITH_UNDERSCORES)"}
                disabled={!!editingCategory}
                InputProps={{
                  readOnly: !!editingCategory,
                }}
              />

              {editingCategory && (
                <>
                  <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#856404', mb: 1 }}>
                      ⚠️ Want to rename the category value?
                    </Typography>
                    <TextField
                      fullWidth
                      label="New Value (Optional)"
                      value={newValueInput}
                      onChange={(e) => setNewValueInput(e.target.value)}
                      placeholder="Leave empty to keep current value"
                      size="small"
                      helperText={newValueInput !== formData.value ? `Will rename from "${formData.value}" to "${newValueInput.toUpperCase().replace(/[^A-Z0-9]/g, '_')}" and update all ${sitesUsingCategory} site(s)` : "Enter a new value to rename this category"}
                    />
                  </Box>
                </>
              )}

              <TextField
                fullWidth
                label="Display Label *"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Technology"
                margin="normal"
                helperText="Display name for the category"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the category"
                margin="normal"
                multiline
                rows={3}
                helperText="Optional description to explain the category"
              />
            </div>

            <div className="category-modal-footer">
              <button
                className="category-btn category-btn-secondary"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                className="category-btn category-btn-primary"
                onClick={handleSubmit}
              >
                {editingCategory ? (
                  <>
                    <SaveIcon />
                    Update Category
                  </>
                ) : (
                  <>
                    <AddIcon />
                    Add Category
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>{`
        .category-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1300;
          padding: 20px;
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .category-modal {
          background: #ffffff;
          border-radius: 12px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow:
            0 10px 25px rgba(0, 0, 0, 0.15),
            0 4px 10px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          position: relative;
          animation: slideUp 0.3s ease-out;
          border: 1px solid #e2e8f0;
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .category-modal-header {
          background: #f8fafc;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .category-modal-title {
          color: #1e293b;
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .category-modal-title::before {
          content: '⚙️';
          font-size: 20px;
        }
        .category-modal-close-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .category-modal-close-btn:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .category-modal-content {
          background: #ffffff;
          padding: 24px;
          flex: 1;
          overflow-y: auto;
        }
        .category-modal-footer {
          background: #f8fafc;
          padding: 16px 24px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 1px solid #e2e8f0;
        }
        .category-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid;
          font-family: inherit;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .category-btn svg {
          width: 16px;
          height: 16px;
        }
        .category-btn-secondary {
          background: #ffffff;
          color: #64748b;
          border-color: #e2e8f0;
        }
        .category-btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .category-btn-primary {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .category-btn-primary:hover {
          background: #2563eb;
          border-color: #2563eb;
        }
      `}</style>
    </>
  );
};

export default CategorySettingsPage;
